/* eslint-disable jsx-a11y/no-autofocus */
import React from 'react';
import styled from 'styled-components';

import { Command, tabActions } from '@store/slices';
import { Option } from '@common/BucketInput/BucketField';
import { Select } from '@common/Select';
import { useNodeData } from '@common/hooks/useNodeData';
import { Stack, alpha } from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import { useTheme } from '../../../../styles';
import { useDispatch } from 'react-redux';
import { Button } from '@common/Button';
import { NodeCondition } from '@components/Flow/Nodes';
import { Expression } from '@common/Expression';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: 8px;
`;

export const ConditionContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: 8px;

  padding-left: 1.25rem;
  margin-left: 0.75rem;
  border-left: 1px solid ${({ theme }) => theme.line.light};
`;

interface Props {
  id: string;
  groupId: string;
  fieldId: string;
  label: string;
  description: string;
  editing?: boolean;
  command: Command;
  condition: NodeCondition;
  removedDisabled: boolean;
}

export const Condition: React.FC<Props> = ({
  id,
  groupId,
  fieldId,
  label,
  description,
  editing,
  command,
  condition,
  removedDisabled,
}) => {
  const { getOptions, dataEdges } = useNodeData(command.node.id);
  const { input, operator, value } = condition;

  const dispatch = useDispatch();
  const theme = useTheme();
  const options = getOptions(command.node.id);

  const searchTypeOptions: Option[] = [
    { label: 'Equals', value: 'equals' },
    { label: 'Is Undefined', value: 'is_undefined' },
    { label: 'Is Defined', value: 'is_defined' },
    { label: 'Starts with', value: 'starts_with' },
    { label: 'Ends with', value: 'ends_with' },
    { label: 'Contains', value: 'contains' },
    { label: 'Greater Than', value: 'greater_than' },
    { label: 'Greater Than or Equal', value: 'greater_than_equal' },
    { label: 'Less Than', value: 'less_than' },
    { label: 'Less Than or Equal', value: 'less_than_equal' },
    { label: 'Does not equal', value: 'does_not_equal' },
    { label: 'Does not start with', value: 'does_not_start_with' },
    { label: 'Does not end with', value: 'does_not_end_with' },
    { label: 'Does not contain', value: 'does_not_contain' },
  ];

  const searchOptionsWithoutValue = ['is_undefined', 'is_defined'];

  const removeConditionFromGroup = () => {
    dispatch(
      tabActions.nodeInputArrayNestedArrayFilter({
        nodeId: command.node.id,
        fieldId,
        nestedFieldId: groupId,
        nestedFieldName: 'conditions',
        childId: id,
      })
    );
    dispatch(tabActions.setSaved(false));
  };

  const updateField = ({ value, attribute }: { value: string; attribute: string }) => {
    if (attribute === 'input' || attribute === 'value')
      dispatch(tabActions.removeNodeFieldErrors({ nodeId: command.node.id, fieldId: groupId }));

    dispatch(
      tabActions.nodeInputArrayNestedArrayUpdate({
        nodeId: command.node.id,
        fieldId,
        nestedFieldId: groupId,
        nestedFieldName: 'conditions',
        childId: id,
        fieldName: attribute,
        value: value,
      })
    );

    dispatch(tabActions.setSaved(false));
  };

  return (
    <Container>
      <ConditionContainer>
        <Expression
          key={`${command.node.id}-input`}
          value={input}
          title={label}
          dataEdges={dataEdges}
          description={description}
          setValue={(value: string) => updateField({ value, attribute: 'input' })}
          options={options}
          placeholder='Input'
        />
        <Select
          key={`${command.node.id}-operator`}
          value={operator as string}
          setValue={(value) => updateField({ value, attribute: 'operator' })}
          options={searchTypeOptions}
          placeholder='Select an index search type'
        />
        {!searchOptionsWithoutValue.includes(operator) && (
          <Expression
            key={`${command.node.id}-value`}
            value={value}
            title={label}
            dataEdges={dataEdges}
            description={description}
            setValue={(value: string) => updateField({ value, attribute: 'value' })}
            options={options}
            placeholder='Value'
          />
        )}
      </ConditionContainer>
      {editing && !removedDisabled && (
        <Stack display='flex' width='100%' alignItems='flex-end'>
          <Button
            onClick={() => removeConditionFromGroup()}
            disabled={removedDisabled}
            sx={{ maxWidth: '100px' }}
            startIcon={<CloseOutlined sx={{ color: alpha(theme.telnyx.orange, 0.75) }} />}
          >
            Delete
          </Button>
        </Stack>
      )}
    </Container>
  );
};
